/**
 * Staff Support Board — Kanban across 4 columns:
 *   New → Awaiting Customer Info → Awaiting Ox Action → Query Complete
 *
 * Priorities inside "New" stack top-down: urgent (rose) → priority (amber) →
 * standard (slate) — same visual language as the Invoice Board. Drag-drop
 * moves a ticket between columns via PATCH /api/support/tickets/:id
 * { status: <target-key> }.
 *
 * Admins/managers get a "New Ticket" button in the header + a trash icon on
 * every card (soft-delete via DELETE /api/support/tickets/:id). Team members
 * can drive the Kanban but not create/delete.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, LifeBuoy, Plus, Trash2, Clock, AlertTriangle, Flame, Zap, CheckCircle2,
  MessageCircle, StickyNote, Search, X, Mail, Truck, BellRing, Hammer, GraduationCap, Filter, Settings, Archive,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { formatApiError } from '@/utils/apiError';
import useHomePath from '@/hooks/useHomePath';
import NewSupportTicketModal from '@/components/support/NewSupportTicketModal';
import SupportRoutingModal from '@/components/support/SupportRoutingModal';
import { QuickNote } from '@/components/support/QuickNote';
import { CardToolbar, ContactDetails, EditableTitle } from '@/components/support/CardControls';
import { SupportAlertsModal } from '@/components/support/SupportAlertsModal';
import { SupportContactModal } from '@/components/support/SupportContactModal';
import { SupportArchive, daysUntil, ARCHIVE_AFTER_DAYS } from '@/components/support/SupportArchive';
import { KanbanColumn, MoveToDropdown, KANBAN_PALETTE } from '@/components/kanban/KanbanShared';
import logger from '@/utils/logger';

const COLUMNS = [
  { key: 'new',                label: 'New',                colour: KANBAN_PALETTE.emerald, icon: Zap },
  { key: 'awaiting_customer',  label: 'Awaiting Customer',  colour: KANBAN_PALETTE.blue,    icon: MessageCircle },
  { key: 'awaiting_ox',        label: 'Awaiting Ox Action', colour: KANBAN_PALETTE.amber,   icon: AlertTriangle },
  { key: 'complete',           label: 'Query Complete',     colour: KANBAN_PALETTE.green,   icon: CheckCircle2 },
];

const PRIORITY_META = {
  urgent:   { label: 'Urgent',   swatch: '#e11d48', chip: 'bg-rose-100 text-rose-800 border-rose-200',       icon: Flame },
  priority: { label: 'Priority', swatch: '#d97706', chip: 'bg-amber-100 text-amber-800 border-amber-200',    icon: Zap },
  standard: { label: 'Standard', swatch: '#64748b', chip: 'bg-slate-100 text-slate-700 border-slate-200',    icon: Clock },
};
const PRIORITY_ORDER = ['urgent', 'priority', 'standard'];
const CATEGORY_LABEL = {
  delivery: 'Delivery',
  installation_support: 'Installation Support',
  training_course: 'Training Course',
};
// Chip row on the board — 'all' clears the filter, 'none' shows category-less
// staff to-dos. Icons chosen to match the picker in NewSupportTicketModal.
const CATEGORY_FILTER_TABS = [
  { key: 'all',                  label: 'All',           icon: Filter,        ring: 'ring-slate-400' },
  { key: 'delivery',             label: 'Delivery',      icon: Truck,         ring: 'ring-emerald-400' },
  { key: 'installation_support', label: 'Installation',  icon: Hammer,        ring: 'ring-sky-400' },
  { key: 'training_course',      label: 'Training',      icon: GraduationCap, ring: 'ring-violet-400' },
  { key: 'none',                 label: 'No category',   icon: StickyNote,    ring: 'ring-slate-400' },
];

const ageHours = (iso) => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : (Date.now() - t) / (1000 * 60 * 60);
};
const fmtAge = (iso) => {
  const h = ageHours(iso);
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
};

export default function SupportBoard() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const { user } = useContext(AuthContext);
  const [contact, setContact] = useState(null); // { ticket, channel }
  const canAdmin = ['admin', 'manager'].includes(user?.role);
  const [ticketsByCol, setTicketsByCol] = useState({ new: [], awaiting_customer: [], awaiting_ox: [], complete: [] });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showRouting, setShowRouting] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [search, setSearch] = useState('');
  // Scope selector — 'all', 'me', or a specific teammate's user_id. Persisted
  // per-user across reloads so a team member who always filters by their own
  // name (or by Steve's) doesn't get bounced back to All every page load.
  const [scope, setScope] = useState(() => localStorage.getItem('support-board-scope') || 'all');
  // Category chip filter — remembered per browser so a refresh keeps the view.
  const [categoryFilter, setCategoryFilter] = useState(() => localStorage.getItem('support-board-category') || 'all');
  const [teammates, setTeammates] = useState([]);
  useEffect(() => { localStorage.setItem('support-board-scope', scope); }, [scope]);
  useEffect(() => { localStorage.setItem('support-board-category', categoryFilter); }, [categoryFilter]);

  // Roster for the scope dropdown — staff only.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/support/staff-members`, { credentials: 'include' });
        if (res.ok) setTeammates(await res.json());
      } catch (err) { logger.warn('[support] failed to load teammates roster:', err); }
    })();
  }, []);

  const scopeParam = scope === 'all' ? '' : scope === 'me' ? 'me' : scope;
  const [showArchive, setShowArchive] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const loadArchivedCount = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/staff/support/summary`, { credentials: 'include' });
      if (r.ok) setArchivedCount((await r.json()).archived || 0);
    } catch (e) { logger.error('support summary', e); }
  }, []);
  useEffect(() => { loadArchivedCount(); }, [loadArchivedCount]);
  const [mentionSummary, setMentionSummary] = useState(null);
  const loadMentions = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/support/mentions/summary`, { credentials: 'include' });
      if (r.ok) setMentionSummary(await r.json());
    } catch (e) { logger.error('mentions summary', e); }
  }, []);
  useEffect(() => { loadMentions(); }, [loadMentions]);
  const activeTeammate = scope !== 'all' && scope !== 'me' ? teammates.find((t) => t.user_id === scope) : null;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const qs = scopeParam ? `?assigned_to=${encodeURIComponent(scopeParam)}` : '';
      const res = await fetch(`${API_URL}/api/support/tickets${qs}`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Load failed');
      const rows = await res.json();
      const grouped = { new: [], awaiting_customer: [], awaiting_ox: [], complete: [] };
      for (const t of rows) if (grouped[t.status]) grouped[t.status].push(t);
      setTicketsByCol(grouped);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [scopeParam]);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    load();
    // Background refresh every 30s — silent (no spinner, cards stay mounted so scroll position and half-typed notes survive),
    // and skipped while the tab is hidden or someone is typing in a card.
    const interval = setInterval(() => {
      if (document.hidden) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      load(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [user, navigate, load]);

  const moveTicket = async (ticket, targetStatus) => {
    if (ticket.status === targetStatus) return;
    // Optimistic move
    const prev = ticketsByCol;
    setTicketsByCol((cur) => {
      const next = { ...cur };
      next[ticket.status] = next[ticket.status].filter((t) => t.id !== ticket.id);
      next[targetStatus] = [{ ...ticket, status: targetStatus }, ...next[targetStatus]];
      return next;
    });
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticket.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: targetStatus }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Update failed');
      toast.success(`${ticket.reference || 'Request'} → ${COLUMNS.find(c => c.key === targetStatus)?.label}`);
    } catch (e) {
      toast.error(e.message);
      setTicketsByCol(prev);
    }
  };

  const softDelete = async (ticket, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!canAdmin) return;
    const prev = ticketsByCol;
    setTicketsByCol((cur) => ({ ...cur, [ticket.status]: cur[ticket.status].filter((t) => t.id !== ticket.id) }));
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticket.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Delete failed');
      toast(`${ticket.reference || 'Request'} deleted`, { duration: 5000 });
    } catch (err) {
      toast.error(err.message);
      setTicketsByCol(prev);
    }
  };

  // Case-insensitive substring match across the fields staff actually search by:
  // reference, installer name, description, and note bodies. Empty query
  // returns everything.
  const matchesSearch = useCallback((t, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    if ((t.reference || '').toLowerCase().includes(needle)) return true;
    if ((t.installer_name || '').toLowerCase().includes(needle)) return true;
    if ((t.description || '').toLowerCase().includes(needle)) return true;
    return (t.notes || []).some((n) => (n.body || '').toLowerCase().includes(needle));
  }, []);

  // Category chip filter: 'all' passes everything, 'none' matches only
  // category-less to-dos, any other value must exactly match `t.category`.
  const matchesCategory = useCallback((t) => {
    if (categoryFilter === 'all') return true;
    if (categoryFilter === 'none') return !t.category;
    return t.category === categoryFilter;
  }, [categoryFilter]);

  // Tickets with un-ticked @pings for me float to the top of every column.
  const myOpenPings = useCallback((t) => (t.notes || []).filter((n) => !n.done_at && (n.mentions || []).some((m) => m.user_id === user?.user_id)).length, [user?.user_id]);

  // Sort each column: pinged-for-me first, then new column by priority order; others by most recent.
  const columns = useMemo(() => COLUMNS.map((c) => {
    const rows = (ticketsByCol[c.key] || []).filter((t) => matchesSearch(t, search.trim()) && matchesCategory(t));
    if (c.key === 'new') {
      return {
        ...c,
        rows: [...rows].sort((a, b) => {
          const pd = (myOpenPings(b) > 0) - (myOpenPings(a) > 0);
          if (pd !== 0) return pd;
          const ap = PRIORITY_ORDER.indexOf(a.priority || 'standard');
          const bp = PRIORITY_ORDER.indexOf(b.priority || 'standard');
          if (ap !== bp) return ap - bp;
          return (b.created_at || '').localeCompare(a.created_at || '');
        }),
      };
    }
    return { ...c, rows: [...rows].sort((a, b) => ((myOpenPings(b) > 0) - (myOpenPings(a) > 0)) || (b.updated_at || '').localeCompare(a.updated_at || '')) };
  }), [ticketsByCol, search, matchesSearch, matchesCategory, myOpenPings]);
  const updateTicket = (id, fn) => setTicketsByCol((cur) => Object.fromEntries(Object.entries(cur).map(([k, list]) => [k, list.map((x) => (x.id === id ? fn(x) : x))])));
  const patchTicket = async (t, body) => {
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${t.id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Update failed');
      const updated = await res.json();
      updateTicket(t.id, (x) => ({ ...x, ...updated }));
      toast.success('Updated');
    } catch (e) { toast.error(e.message); }
  };

  const totalFiltered = columns.reduce((n, c) => n + c.rows.length, 0);
  const totalAll = Object.values(ticketsByCol).reduce((n, arr) => n + arr.length, 0);

  // Chip badges: count of *open* tickets (new + awaiting_*) per category that
  // also satisfy the current search — so staff can see at a glance how many
  // Delivery / Installation / Training requests are outstanding before
  // deciding whether to filter. `complete` is excluded because a huge pile
  // of resolved requests would make the badges misleading.
  const categoryCounts = useMemo(() => {
    const openTickets = [
      ...(ticketsByCol.new || []),
      ...(ticketsByCol.awaiting_customer || []),
      ...(ticketsByCol.awaiting_ox || []),
    ].filter((t) => matchesSearch(t, search.trim()));
    return {
      all: openTickets.length,
      delivery: openTickets.filter((t) => t.category === 'delivery').length,
      installation_support: openTickets.filter((t) => t.category === 'installation_support').length,
      training_course: openTickets.filter((t) => t.category === 'training_course').length,
      none: openTickets.filter((t) => !t.category).length,
    };
  }, [ticketsByCol, search, matchesSearch]);

  const moveOptions = COLUMNS.map((c) => ({ key: c.key, label: c.label }));

  const renderCard = (t) => {
    const pMeta = PRIORITY_META[t.priority] || PRIORITY_META.standard;
    const PIcon = pMeta.icon;
    return (
      <div
        key={t.id}
        className="group relative bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition"
        style={{ borderLeft: `4px solid ${pMeta.swatch}` }}
        data-testid={`support-card-${t.id}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 border ${pMeta.chip}`}>
            <PIcon className="w-3 h-3" /> {pMeta.label}
          </span>
          {t.category && CATEGORY_LABEL[t.category] && (
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-900 text-white"
              title={CATEGORY_LABEL[t.category]}
            >
              {CATEGORY_LABEL[t.category]}
            </span>
          )}
          <span className="font-mono text-[11px] text-gray-500 truncate flex-1 min-w-0">{t.reference}</span>
          {canAdmin && (
            <button
              type="button"
              onClick={(e) => softDelete(t, e)}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded p-1 -mr-1 -my-1 transition"
              data-testid={`support-delete-${t.id}`}
              title="Delete request"
              aria-label="Delete request"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <EditableTitle ticket={t} onPatch={patchTicket} />
        <div className="text-[11px] text-slate-500 truncate" title={t.installer_name}>
          {t.installer_name || 'No installer'}
        </div>
        <CardToolbar ticket={t} teammates={teammates} priorities={PRIORITY_ORDER.map((k) => ({ key: k, label: PRIORITY_META[k].label }))} categories={Object.entries(CATEGORY_LABEL).map(([key, label]) => ({ key, label }))} onPatch={patchTicket} />
        <div className="mt-1.5 flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1 mb-0.5 flex-wrap">
            <button type="button" onClick={() => setContact({ ticket: t, channel: 'sms' })} disabled={!t.installer_phone} title={t.installer_phone ? 'Text the installer' : 'Add a phone number first'} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-40" data-testid={`support-sms-${t.id}`}><MessageCircle className="w-3 h-3" /> Text</button>
            <button type="button" onClick={() => setContact({ ticket: t, channel: 'email' })} disabled={!t.installer_email} title={t.installer_email ? 'Email the installer' : 'Add an email first'} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 disabled:opacity-40" data-testid={`support-mail-${t.id}`}><Mail className="w-3 h-3" /> Email</button>
            {t.last_contact_channel && <span className="text-[10px] text-gray-400 self-center" title={t.last_contacted_at}>last {t.last_contact_channel === 'sms' ? 'texted' : 'emailed'} {t.last_contacted_at ? new Date(t.last_contacted_at).toLocaleDateString('en-GB') : ''}</span>}
          </div>
          <ContactDetails ticket={t} onPatch={patchTicket} />
        </div>
        <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap break-words" data-testid={`support-description-${t.id}`}>{t.description}</div>
        <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500 gap-2">
          <span className="inline-flex items-center gap-1 shrink-0">
            <Clock className="w-3 h-3" /> {fmtAge(t.created_at)}
            {t.status === 'complete' && t.completed_at && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700" title="Completed queries move to the archive after 2 days" data-testid={`support-archives-in-${t.id}`}>
                <Archive className="w-3 h-3" /> {daysUntil(t.completed_at, ARCHIVE_AFTER_DAYS) === 0 ? 'archiving soon' : `archives in ${daysUntil(t.completed_at, ARCHIVE_AFTER_DAYS)}d`}
              </span>
            )}
          </span>
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            {(t.attachments?.length || 0) > 0 && (
              <span className="inline-flex items-center gap-0.5 text-slate-600">📎 {t.attachments.length}</span>
            )}
            <span className="inline-flex items-center gap-0.5 text-slate-600" title="Notes" data-testid={`support-notes-count-${t.id}`}>
              <StickyNote className="w-3 h-3" /> {t.notes?.length || 0}
            </span>
            {myOpenPings(t) > 0 && (
              <span className="inline-flex items-center text-[10px] font-bold text-white bg-[#7A0B7A] rounded-full px-1.5" title="Un-ticked @pings for you" data-testid={`support-ping-badge-${t.id}`}>@ {myOpenPings(t)}</span>
            )}
          </div>
        </div>
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <QuickNote ticket={t} canDelete={canAdmin}
            onAdded={(note) => updateTicket(t.id, (x) => ({ ...x, notes: [...(x.notes || []), note] }))}
            onUploaded={(atts) => updateTicket(t.id, (x) => ({ ...x, attachments: [...(x.attachments || []), ...atts] }))}
            onNoteChanged={(note) => updateTicket(t.id, (x) => ({ ...x, notes: (x.notes || []).map((n) => (n.id === note.id ? note : n)) }))}
            onNoteDeleted={(id) => updateTicket(t.id, (x) => ({ ...x, notes: (x.notes || []).filter((n) => n.id !== id) }))} />
        </div>
        <div className="mt-2">
          <MoveToDropdown
            current={t.status}
            options={moveOptions}
            onChange={(target) => moveTicket(t, target)}
            testId={`support-move-${t.id}`}
          />
        </div>
      </div>
    );
  };

  const totalOpen = (ticketsByCol.new.length + ticketsByCol.awaiting_customer.length + ticketsByCol.awaiting_ox.length);

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0f172a, #334155)' }}>
        <div className="max-w-[1600px] mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/staff')}
            data-testid="support-board-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back to Staff Dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <LifeBuoy className="w-5 h-5 shrink-0" /> Support Board
            </h1>
            <p className="hidden sm:block text-white/70 text-xs">
              {search.trim()
                ? `${totalFiltered} of ${totalAll} matching "${search.trim()}"`
                : scope === 'mentioned'
                  ? `${totalOpen + ticketsByCol.complete.length} tickets you've been @mentioned on · ${mentionSummary?.open_pings ?? 0} pings not yet ticked done`
                : scope === 'me'
                  ? `${totalOpen} open assigned to you · ${ticketsByCol.complete.length} complete`
                  : activeTeammate
                    ? `${totalOpen} open assigned to ${activeTeammate.name} · ${ticketsByCol.complete.length} complete`
                    : `${totalOpen} open · ${ticketsByCol.complete.length} complete · Standard auto-bumps to Priority after 24h`}
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-1 shrink-0" role="group" aria-label="Ticket scope filter">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              data-testid="support-scope-select"
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs font-semibold text-white focus:outline-none focus:bg-white/20 focus:border-white/40 cursor-pointer max-w-[220px]"
              aria-label="Filter requests by assignee"
            >
              <option value="all" className="text-slate-900">All requests</option>
              <option value="me" className="text-slate-900">Assigned to me</option>
              <option value="mentioned" className="text-slate-900">Mentioned me{mentionSummary?.open_pings ? ` (${mentionSummary.open_pings} open)` : ''}</option>
              {teammates.length > 0 && (
                <optgroup label="Assigned to teammate">
                  {teammates.map((t) => (
                    <option key={t.user_id} value={t.user_id} className="text-slate-900">{t.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div className="relative shrink-0 hidden sm:block">
            <Search className="w-4 h-4 text-white/60 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search requests & notes…"
              data-testid="support-board-search"
              className="pl-9 pr-8 py-2 bg-white/10 border border-white/20 rounded-lg text-sm placeholder-white/50 text-white w-56 md:w-72 focus:outline-none focus:bg-white/20 focus:border-white/40"
              aria-label="Search requests and notes"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                data-testid="support-board-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            onClick={() => setShowArchive((v) => !v)}
            size="sm"
            variant="outline"
            className={`border-white/30 text-white hover:bg-white/20 shrink-0 ${showArchive ? 'bg-white/30' : 'bg-white/10'}`}
            data-testid="support-board-archive-toggle"
            aria-pressed={showArchive}
            title={showArchive ? 'Back to board' : 'Archived queries'}
          >
            <Archive className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{showArchive ? 'Board' : 'Archive'}</span>
            {archivedCount > 0 && <span className="ml-1 text-[10px] font-bold bg-white text-slate-900 rounded-full px-1.5" data-testid="support-board-archive-count">{archivedCount}</span>}
          </Button>
          {canAdmin && (
            <Button
              onClick={() => setShowAlerts(true)}
              size="sm"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
              data-testid="support-board-alerts"
              aria-label="Who gets support alerts"
              title="Who gets texts & emails for new queries"
            >
              <BellRing className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Alerts</span>
            </Button>
          )}
          {canAdmin && (
            <Button
              onClick={() => setShowRouting(true)}
              size="sm"
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
              data-testid="support-board-routing"
              aria-label="Auto-route settings"
              title="Auto-route settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
          {canAdmin && (
            <Button
              onClick={() => setShowNew(true)}
              size="sm"
              className="bg-white text-slate-900 hover:bg-slate-100 shrink-0"
              data-testid="support-board-new-ticket"
            >
              <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">New Request</span>
            </Button>
          )}
          <Button
            onClick={() => navigate(homePath)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 hidden sm:inline-flex"
            aria-label="Home"
          >
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-4">
        {!showArchive && (<>
        {/* Category filter chips — one-tap way to zero in on Delivery / Installation / Training. */}
        <div
          className="mb-3 -mx-1 px-1 flex items-center gap-2 overflow-x-auto snap-x pb-1"
          role="tablist"
          aria-label="Filter requests by category"
          data-testid="support-category-chips"
        >
          {CATEGORY_FILTER_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = categoryFilter === tab.key;
            const count = categoryCounts[tab.key] || 0;
            // Hide the "No category" chip when there's nothing without a
            // category — keeps the row tidy for installer-only pipelines.
            if (tab.key === 'none' && count === 0 && !active) return null;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setCategoryFilter(tab.key)}
                data-testid={`support-category-chip-${tab.key}`}
                className={`snap-start shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  active
                    ? `bg-slate-900 text-white border-slate-900 ring-2 ${tab.ring}`
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                    active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Mobile scope toggle + search — the desktop pair lives inline in the header */}
        <div className="sm:hidden mb-3 space-y-2">
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            data-testid="support-scope-select-mobile"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold"
            aria-label="Filter requests by assignee"
          >
            <option value="all">All requests</option>
            <option value="me">Assigned to me</option>
            <option value="mentioned">Mentioned me{mentionSummary?.open_pings ? ` (${mentionSummary.open_pings})` : ''}</option>
            {teammates.length > 0 && (
              <optgroup label="Assigned to teammate">
                {teammates.map((t) => (
                  <option key={t.user_id} value={t.user_id}>{t.name}</option>
                ))}
              </optgroup>
            )}
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search requests & notes…"
              data-testid="support-board-search-mobile"
              className="pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-lg text-sm w-full focus:outline-none focus:border-slate-400"
              aria-label="Search requests and notes"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        </>)}
        {showArchive ? (
          <SupportArchive canAdmin={canAdmin} onChanged={() => { load(true); loadArchivedCount(); }} />
        ) : loading ? (
          <Card className="p-10 text-center text-gray-500">Loading support requests…</Card>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {columns.map((col) => (
                <KanbanColumn
                  key={col.key}
                  column={col}
                  count={col.rows.length}
                  testId={`support-col-${col.key}`}
                  emptyLabel={
                    search.trim()
                      ? 'No matches'
                      : scope === 'mentioned' ? 'Nobody has @mentioned you yet'
                      : scope === 'me' ? 'Nothing assigned to you'
                      : activeTeammate ? `Nothing assigned to ${activeTeammate.name}`
                      : 'No requests'
                  }
                >
                  {col.rows.map(renderCard)}
                </KanbanColumn>
              ))}
            </div>
          </div>
        )}
      </div>

      <SupportContactModal open={!!contact} ticket={contact?.ticket} channel={contact?.channel || 'sms'} staffName={user?.name} onClose={() => setContact(null)} onSent={() => load(true)} />
      {showNew && (
        <NewSupportTicketModal
          isStaff
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(true); }}
        />
      )}
      {showRouting && (
        <SupportRoutingModal onClose={() => setShowRouting(false)} />
      )}
      {showAlerts && (
        <SupportAlertsModal onClose={() => setShowAlerts(false)} />
      )}
    </div>
  );
}
